import { useCallback } from 'react';
import { graphql, useRelayEnvironment } from 'react-relay';
import braintreeClient from 'braintree-web/client';
import commitMutation from 'relay-commit-mutation-promise';
import creditCardValidator, { getExpiryMonth, getExpiryYear } from 'dibs-credit-card-validator';
import { executeCaptcha } from 'dibs-recaptcha/exports/recaptcha';
import { PaymentMethod } from '../../types';
import { CreditCardForm, usePaymentMethodSelectContext } from '../PaymentMethodSelectContext';
import { type SubmitResult, createSubmitError } from '../submitPaymentMethod';
import { guestWalletUpdater } from '../wallet/guestWalletUpdater';

import { submitCreditCardVaultMutation } from './__generated__/submitCreditCardVaultMutation.graphql';

const submitCreditCardVaultMutation = graphql`
    mutation submitCreditCardVaultMutation($input: UserVaultPaymentMethodInput!) {
        userVaultPaymentMethod(input: $input) {
            user {
                wallet {
                    paymentMethods {
                        currencies
                        paymentMethod {
                            paymentMethodType
                            ... on CreditCard {
                                token
                                paymentProcessor
                                cardLogo
                                cardType
                                cardExpiryMonth
                                cardExpiryYear
                                formattedTextMaskedCardNumber
                                hashedCardNumber
                            }
                            ... on PayPalAccount {
                                token
                                email
                                paymentProcessor
                            }
                            ... on ACH {
                                token
                                paymentProcessor
                                accountType
                                last4
                                accountHolderName
                                routingNumber
                                display
                            }
                            ... on ApplePay {
                                token
                                paymentProcessor
                            }
                        }
                        shippingAddresses {
                            serviceId
                        }
                    }
                }
            }
            walletPaymentMethod {
                currencies
                paymentMethod {
                    ... on CreditCard {
                        token
                    }
                }
                shippingAddresses {
                    serviceId
                }
            }
        }
    }
`;

export function useCreditCardSubmit(): (paymentMethod: PaymentMethod) => Promise<SubmitResult> {
    const {
        creditCardForm,
        updateCreditCardFormErrors,
        braintreeClientToken,
        viewerId,
        billingAddress,
        mockWallet,
        userId,
        paymentMethodOptions,
        selectedPaymentMethod,
    } = usePaymentMethodSelectContext();

    const { isVerifying, cardHolderName, creditCardNumber, cardExpiry, securityCode } =
        creditCardForm;

    // verification should use the payment method's billing address
    const relevantBillingAddress = isVerifying
        ? selectedPaymentMethod?.billingAddress
        : billingAddress;
    const { firstName, lastName, company, line1, line2, state, city, zipCode, country } =
        relevantBillingAddress || {};

    const shouldVerifyPaymentMethod = paymentMethodOptions?.CREDIT_CARD?.verifyPaymentMethod;

    const relayEnvironment = useRelayEnvironment();

    return useCallback(
        async paymentMethod => {
            if (!userId) {
                return createSubmitError(
                    'IMPLEMENTATION',
                    'userId is required to submit credit cards'
                );
            }

            updateCreditCardFormErrors({});

            if (paymentMethod?.paymentMethodToken && !isVerifying) {
                // using vaulted card
                return { paymentMethod };
            }

            const creditCard = {
                cardHolderName,
                creditCardNumber,
                cardExpiry,
                securityCode,
            };

            const { errors, isValid } = creditCardValidator(creditCard);

            if (!isValid) {
                updateCreditCardFormErrors(
                    // reduces validator errors object `{ field: { defaultMessage: "message" } }` to  local `{ field: "message" }`;
                    Object.keys(errors).reduce<CreditCardForm>((currentErrorsObject, field) => {
                        currentErrorsObject[field as keyof CreditCardForm] =
                            errors[field]?.defaultMessage;
                        return currentErrorsObject;
                    }, {})
                );
                return createSubmitError('VALIDATION', 'Credit card form invalid', {
                    creditCardErrors: errors,
                });
            }

            const creditCardData = {
                number: (creditCardNumber || '').replace(/ /g, ''),
                cardholderName: cardHolderName,
                expirationMonth: getExpiryMonth(cardExpiry),
                expirationYear: getExpiryYear(cardExpiry),
                cvv: securityCode,
                billingAddress: {
                    firstName: firstName || undefined,
                    lastName: lastName || undefined,
                    company: company || undefined,
                    streetAddress: line1,
                    extendedAddress: line2 || undefined,
                    locality: city,
                    region: state || '',
                    postalCode: zipCode,
                    countryCodeAlpha2: country,
                },
            };

            const captchaResults = await executeCaptcha({
                action: 'checkoutVaultCreditCard',
                featureFlag: 'vaultCreditCardCaptcha',
                featureFlagV3: 'vaultCreditCardCaptchaV3',
                relayEnvironment,
            });

            const clientInstance = await braintreeClient.create({
                authorization: braintreeClientToken,
            });

            const braintreeResponse = await clientInstance.request({
                endpoint: 'payment_methods/credit_cards',
                method: 'post',
                data: {
                    creditCard: creditCardData,
                    options: {
                        validate: false,
                    },
                },
            });

            const response = await commitMutation<submitCreditCardVaultMutation>(relayEnvironment, {
                mutation: submitCreditCardVaultMutation,
                variables: {
                    input: {
                        userId: userId,
                        saveToWallet: !mockWallet,
                        mockWallet: !!mockWallet,
                        paymentMethodNonce: braintreeResponse?.creditCards?.[0]?.nonce,
                        verifyPaymentMethod: shouldVerifyPaymentMethod,
                        captchaToken: captchaResults?.captchaToken,
                        captchaKey: captchaResults?.captchaKey,
                    },
                },
                updater:
                    mockWallet && viewerId
                        ? store =>
                              guestWalletUpdater<submitCreditCardVaultMutation>(
                                  store,
                                  viewerId,
                                  userId,
                                  'userVaultPaymentMethod'
                              )
                        : null,
            });

            const vaultedPaymentMethod =
                response?.userVaultPaymentMethod?.walletPaymentMethod?.paymentMethod || {};
            const paymentMethodToken = vaultedPaymentMethod?.token;

            if (!paymentMethodToken) {
                return createSubmitError('VAULT', 'Unable to get vaulted credit card token');
            }

            return {
                paymentMethod: {
                    paymentMethodType: 'CREDIT_CARD',
                    paymentProcessor: 'BRAINTREE',
                    paymentMethodToken,
                },
            };
        },
        [
            cardHolderName,
            creditCardNumber,
            cardExpiry,
            securityCode,
            updateCreditCardFormErrors,
            relayEnvironment,
            braintreeClientToken,
            firstName,
            lastName,
            company,
            line1,
            line2,
            state,
            city,
            zipCode,
            country,
            mockWallet,
            userId,
            shouldVerifyPaymentMethod,
            viewerId,
            isVerifying,
        ]
    );
}
